/**
 * Content Hub API Service
 * Provides methods for managing content folders, items, and search functionality.
 */

import { api, handleApiError } from '../client'
import { buildODataQuery, type ODataQueryParams } from '../odata/query-builder'
import type {
  // Folder types
  ContentFolder,
  ContentFolderDetail,
  ContentFolderTree,
  ContentFoldersODataResponse,
  CreateContentFolderRequest,
  UpdateContentFolderRequest,
  MoveFolderRequest,
  // Item types
  ContentItem,
  ContentItemDetail,
  ContentItemsODataResponse,
  ContentItemUsageStats,
  CreateContentItemRequest,
  UpdateContentItemRequest,
  MoveContentItemRequest,
  DuplicateContentItemRequest,
  ContentItemType,
  // Search types
  ContentTag,
  ContentSearchParams,
  ContentSearchResult,
  AddTagsRequest,
} from '../types'

// ============================================
// Folder API Methods
// ============================================

export const contentHubService = {
  // ----------------------------------------
  // Folder Methods
  // ----------------------------------------

  /**
   * Gets root folders (folders without a parent).
   */
  async getRootFolders(): Promise<ContentFolder[]> {
    try {
      const response = await api.get<ContentFolder[]>('/v1/content-hub/folders')
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Gets a folder by ID with children and breadcrumbs.
   */
  async getFolder(id: string): Promise<ContentFolderDetail> {
    try {
      const response = await api.get<ContentFolderDetail>(`/v1/content-hub/folders/${id}`)
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Gets the full folder tree (hierarchical structure).
   */
  async getFolderTree(): Promise<ContentFolderTree[]> {
    try {
      const response = await api.get<ContentFolderTree[]>('/v1/content-hub/folders/tree')
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Gets folders with OData query support (for flat listing).
   */
  async getFolders(params?: ODataQueryParams): Promise<ContentFoldersODataResponse> {
    try {
      const queryString = params ? buildODataQuery(params) : ''
      const response = await api.get<ContentFoldersODataResponse | ContentFolder[]>(
        `/v1/content-hub/folders${queryString}`
      )

      // Handle both OData response and array response for backwards compatibility
      if (Array.isArray(response.data)) {
        return {
          value: response.data,
          '@odata.count': response.data.length,
        }
      }
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Creates a new folder.
   */
  async createFolder(data: CreateContentFolderRequest): Promise<ContentFolder> {
    try {
      const response = await api.post<ContentFolder>('/v1/content-hub/folders', data)
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Updates an existing folder.
   */
  async updateFolder(id: string, data: UpdateContentFolderRequest): Promise<ContentFolder> {
    try {
      const response = await api.put<ContentFolder>(`/v1/content-hub/folders/${id}`, data)
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Deletes a folder.
   * @param id The folder ID
   * @param permanent Whether to permanently delete (default: true)
   * @param recursive Whether to delete children recursively (default: true)
   */
  async deleteFolder(id: string, permanent = true, recursive = true): Promise<void> {
    try {
      await api.delete(`/v1/content-hub/folders/${id}?permanent=${permanent}&recursive=${recursive}`)
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Moves a folder to a new parent (or root if parentId is null).
   */
  async moveFolder(id: string, data: MoveFolderRequest): Promise<ContentFolder> {
    try {
      const response = await api.post<ContentFolder>(`/v1/content-hub/folders/${id}/move`, data)
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  // ----------------------------------------
  // Content Item Methods
  // ----------------------------------------

  /**
   * Gets content items with query support.
   * Uses the backend's native query parameters.
   */
  async getItems(params?: ODataQueryParams): Promise<ContentItemsODataResponse> {
    try {
      // Build query params for the backend's native API format
      const queryParams = new URLSearchParams()

      if (params?.top) {
        queryParams.set('take', String(params.top))
      }
      if (params?.skip) {
        queryParams.set('skip', String(params.skip))
      }
      if (params?.orderBy && params.orderBy.length > 0) {
        const firstOrderBy = params.orderBy[0]
        if (firstOrderBy) {
          queryParams.set('sortBy', firstOrderBy.field)
          queryParams.set('sortDescending', String(firstOrderBy.direction === 'desc'))
        }
      }
      if (params?.filter) {
        // Handle type filter
        const typeFilter = params.filter.find(f => f.field === 'type')
        if (typeFilter) {
          queryParams.set('contentType', String(typeFilter.value))
        }
        // Handle search/name filter
        const nameFilter = params.filter.find(f => f.field === 'name' && f.operator === 'contains')
        if (nameFilter) {
          queryParams.set('searchTerm', String(nameFilter.value))
        }
      }

      const queryString = queryParams.toString()
      const response = await api.get<{ items: ContentItem[]; totalCount: number }>(
        `/v1/content-hub/items${queryString ? `?${queryString}` : ''}`
      )

      return {
        value: response.data.items,
        '@odata.count': response.data.totalCount,
      }
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Gets content items in a specific folder.
   * Uses the backend's native query parameters instead of OData.
   */
  async getItemsByFolder(folderId: string, params?: ODataQueryParams): Promise<ContentItemsODataResponse> {
    try {
      // Build query params for the backend's native API format
      const queryParams = new URLSearchParams()
      queryParams.set('folderId', folderId)

      // Map OData params to backend params
      if (params?.top) {
        queryParams.set('take', String(params.top))
      }
      if (params?.skip) {
        queryParams.set('skip', String(params.skip))
      }
      if (params?.orderBy && params.orderBy.length > 0) {
        const firstOrderBy = params.orderBy[0]
        if (firstOrderBy) {
          queryParams.set('sortBy', firstOrderBy.field)
          queryParams.set('sortDescending', String(firstOrderBy.direction === 'desc'))
        }
      }
      if (params?.filter) {
        // Handle type filter
        const typeFilter = params.filter.find(f => f.field === 'type')
        if (typeFilter) {
          queryParams.set('contentType', String(typeFilter.value))
        }
      }
      if (params?.count) {
        // Backend always returns count, no need to set
      }

      const response = await api.get<{ items: ContentItem[]; totalCount: number }>(
        `/v1/content-hub/items?${queryParams.toString()}`
      )

      return {
        value: response.data.items,
        '@odata.count': response.data.totalCount,
      }
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Gets content items by type.
   * Uses the backend's native query parameters.
   */
  async getItemsByType(type: ContentItemType, params?: ODataQueryParams): Promise<ContentItemsODataResponse> {
    try {
      // Build query params for the backend's native API format
      const queryParams = new URLSearchParams()
      queryParams.set('contentType', type)

      if (params?.top) {
        queryParams.set('take', String(params.top))
      }
      if (params?.skip) {
        queryParams.set('skip', String(params.skip))
      }
      if (params?.orderBy && params.orderBy.length > 0) {
        const firstOrderBy = params.orderBy[0]
        if (firstOrderBy) {
          queryParams.set('sortBy', firstOrderBy.field)
          queryParams.set('sortDescending', String(firstOrderBy.direction === 'desc'))
        }
      }

      const response = await api.get<{ items: ContentItem[]; totalCount: number }>(
        `/v1/content-hub/items?${queryParams.toString()}`
      )

      return {
        value: response.data.items,
        '@odata.count': response.data.totalCount,
      }
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Gets a single content item by ID with full details.
   */
  async getItem(id: string): Promise<ContentItemDetail> {
    try {
      const response = await api.get<ContentItemDetail>(`/v1/content-hub/items/${id}`)
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Creates a new content item.
   */
  async createItem(data: CreateContentItemRequest): Promise<ContentItem> {
    try {
      const response = await api.post<ContentItem>('/v1/content-hub/items', data)
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Updates an existing content item.
   */
  async updateItem(id: string, data: UpdateContentItemRequest): Promise<ContentItem> {
    try {
      const response = await api.put<ContentItem>(`/v1/content-hub/items/${id}`, data)
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Deletes a content item.
   */
  async deleteItem(id: string): Promise<void> {
    try {
      await api.delete(`/v1/content-hub/items/${id}`)
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Moves a content item to a new folder.
   */
  async moveItem(id: string, data: MoveContentItemRequest): Promise<ContentItem> {
    try {
      const response = await api.post<ContentItem>(`/v1/content-hub/items/${id}/move`, data)
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Duplicates a content item.
   */
  async duplicateItem(id: string, data: DuplicateContentItemRequest): Promise<ContentItem> {
    try {
      const response = await api.post<ContentItem>(`/v1/content-hub/items/${id}/duplicate`, data)
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Gets usage statistics for a content item.
   */
  async getItemUsage(id: string): Promise<ContentItemUsageStats> {
    try {
      const response = await api.get<ContentItemUsageStats>(`/v1/content-hub/items/${id}/usage`)
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  // ----------------------------------------
  // Search and Tag Methods
  // ----------------------------------------

  /**
   * Gets all tags used in the Content Hub.
   */
  async getTags(): Promise<ContentTag[]> {
    try {
      const response = await api.get<{ tags: ContentTag[]; totalCount: number }>('/v1/content-hub/tags')
      return response.data.tags
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Searches content items with full-text search and filtering.
   */
  async search(params: ContentSearchParams): Promise<ContentSearchResult> {
    try {
      const queryParams = new URLSearchParams()

      if (params.query) {
        queryParams.set('query', params.query)
      }
      if (params.types && params.types.length > 0) {
        params.types.forEach(type => queryParams.append('types', type))
      }
      if (params.folderId) {
        queryParams.set('folderId', params.folderId)
      }
      if (params.tags && params.tags.length > 0) {
        params.tags.forEach(tag => queryParams.append('tags', tag))
      }
      if (params.includeInactive !== undefined) {
        queryParams.set('includeInactive', String(params.includeInactive))
      }
      if (params.page !== undefined) {
        queryParams.set('page', String(params.page))
      }
      if (params.pageSize !== undefined) {
        queryParams.set('pageSize', String(params.pageSize))
      }
      if (params.sortBy) {
        queryParams.set('sortBy', params.sortBy)
      }
      if (params.sortOrder) {
        queryParams.set('sortOrder', params.sortOrder)
      }

      const queryString = queryParams.toString()
      const url = `/v1/content-hub/search${queryString ? `?${queryString}` : ''}`

      const response = await api.get<ContentSearchResult>(url)
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Adds tags to a content item.
   */
  async addTags(itemId: string, data: AddTagsRequest): Promise<ContentItem> {
    try {
      const response = await api.post<ContentItem>(
        `/v1/content-hub/items/${itemId}/tags`,
        data
      )
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Adds a single tag to a content item.
   */
  async addTag(itemId: string, tag: string): Promise<ContentItem> {
    try {
      const response = await api.put<ContentItem>(
        `/v1/content-hub/items/${itemId}/tags/${encodeURIComponent(tag)}`
      )
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Removes a tag from a content item.
   */
  async removeTag(itemId: string, tag: string): Promise<ContentItem> {
    try {
      const response = await api.delete<ContentItem>(
        `/v1/content-hub/items/${itemId}/tags/${encodeURIComponent(tag)}`
      )
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  // ----------------------------------------
  // Convenience Methods
  // ----------------------------------------

  /**
   * Gets recent content items (sorted by updatedAt desc).
   */
  async getRecentItems(limit: number = 10): Promise<ContentItem[]> {
    try {
      const result = await this.getItems({
        orderBy: [{ field: 'updatedAt', direction: 'desc' }],
        top: limit,
      })
      return result.value
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Gets frequently used content items (sorted by usageCount desc).
   */
  async getPopularItems(limit: number = 10): Promise<ContentItem[]> {
    try {
      const result = await this.getItems({
        orderBy: [{ field: 'usageCount', direction: 'desc' }],
        top: limit,
      })
      return result.value
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Quick search by name (partial match).
   */
  async quickSearch(query: string, limit: number = 20): Promise<ContentItem[]> {
    try {
      const result = await this.search({
        query,
        pageSize: limit,
        sortBy: 'name',
        sortOrder: 'asc',
      })
      return result.items
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },
}

// Export types for consumers
export type {
  ContentFolder,
  ContentFolderDetail,
  ContentFolderTree,
  ContentFoldersODataResponse,
  CreateContentFolderRequest,
  UpdateContentFolderRequest,
  MoveFolderRequest,
  ContentItem,
  ContentItemDetail,
  ContentItemsODataResponse,
  ContentItemUsageStats,
  CreateContentItemRequest,
  UpdateContentItemRequest,
  MoveContentItemRequest,
  DuplicateContentItemRequest,
  ContentItemType,
  ContentTag,
  ContentSearchParams,
  ContentSearchResult,
  AddTagsRequest,
}
